<script setup>
  import { onMounted, ref, defineProps, defineEmits, defineExpose, watch, nextTick, onBeforeUnmount, computed } from 'vue';
  import { useIcons } from '@/composables/icons.js';
  
  const { User } = useIcons();
  
  const props = defineProps({
    modelValue: {
      type: [String, Number],
      default: ''
    },
    options: {
      type: Array,
      required: true
    },
    type: {
      type: String,
      default: 'text'
    },
    id: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    showProfilePicture: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    hideSearch: {
      type: Boolean,
      default: false
    },
    dataTestId: {
      type: String,
      required: false,
      default: ''
    },
    bgClass: {
      type: String,
      default: '#0F0F0F'
    },
    borderClass: {
      type: String,
      default: 'border-[#0F0F0F]'
    },
    hoverBgClass: {
      type: String,
      default: 'bg-echoes-background-black'
    },
    hoverBorderClass: {
      type: String,
      default: 'border-white/20'
    },
    hoverTextClass: {
      type: String,
      default: 'text-white'
    },
    focusBorderClass: {
      type: String,
      default: 'border-echoes-primary'
    },
    dropdownBgClass: {
      type: String,
      default: 'bg-echoes-background-black'
    },
    dropdownBorderClass: {
      type: String,
      default: 'border-[#0F0F0F]'
    },
    optionHoverBgClass: {
      type: String,
      default: 'bg-echoes-background-gray/30'
    },
    avatarBgClass: {
      type: String,
      default: 'bg-echoes-primary'
    }
  });
  
  const emit = defineEmits(['update:modelValue']);
  const input = ref(null);
  const isOpen = ref(false);
  const containerRef = ref(null);
  const dropdownRef = ref(null);
  const searchQuery = ref('');
  const isDropdownUp = ref(false);
  const dropdownStyles = ref({
    top: '0px',
    left: '0px',
    width: '0px'
  });
  
  const displayValue = computed(() => {
    const selectedOption = props?.options?.find(option => option?.value === props?.modelValue);
    return selectedOption ? selectedOption?.displayValue || selectedOption?.label : props?.placeholder;
  });
  
  const filteredOptions = computed(() => {
    if (!searchQuery.value) return props.options;
    const query = searchQuery.value.toLowerCase();
    return props.options.filter(option =>
      option.label.toLowerCase().includes(query)
    );
  });
  
  const selectedOptionWithImage = computed(() => {
    return props?.options?.find(option => option?.value === props?.modelValue);
  });
  
  const selectClasses = computed(() => {
    return [
      'select__select',
      props.bgClass,
      props.borderClass,
      `hover:${props.hoverBgClass}`,
      `hover:${props.hoverBorderClass}`,
      `hover:${props.hoverTextClass}`,
      `focus:${props.focusBorderClass}`
    ];
  });
  
  const dropdownClasses = computed(() => {
    return [
      'select__dropdown',
      props.dropdownBgClass,
      props.dropdownBorderClass
    ];
  });
  
  const avatarClasses = computed(() => {
    return [
      'select__value-avatar',
      props.avatarBgClass
    ];
  });
  
  const optionAvatarClasses = computed(() => {
    return [
      'select__option-avatar',
      props.avatarBgClass
    ];
  });
  
  const handleSelect = (option) => {
    if (props.disabled || props.loading) return;
    emit('update:modelValue', option.value);
    isOpen.value = false;
  };
  
  const toggleDropdown = () => {
    if (props.disabled || props.loading) return;
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
      searchQuery.value = '';
    }
  };
  
  const updateDropdownPosition = () => {
    if (!isOpen.value || !containerRef.value || !dropdownRef.value) return;

    const rect = containerRef.value.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    const dropdownHeight = dropdownRef.value.offsetHeight || 200;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      isDropdownUp.value = true;
      dropdownStyles.value = {
        top: `${rect.top - dropdownHeight - 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`
      };
    } else {
      isDropdownUp.value = false;
      dropdownStyles.value = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`
      };
    }
  };

  const lockBodyScroll = () => {
    const body = document.body;
    const current = Number(body.dataset.selectScrollLockCount || '0');
    if (current === 0) {
      body.classList.add('overflow-hidden');
    }
    body.dataset.selectScrollLockCount = String(current + 1);
  };

  const unlockBodyScroll = () => {
    const body = document.body;
    const current = Number(body.dataset.selectScrollLockCount || '0');
    const next = Math.max(0, current - 1);
    if (next === 0) {
      body.classList.remove('overflow-hidden');
      delete body.dataset.selectScrollLockCount;
    } else {
      body.dataset.selectScrollLockCount = String(next);
    }
  };
  
  watch(isOpen, (newValue) => {
    if (newValue) {
      nextTick(() => {
        updateDropdownPosition();
        lockBodyScroll();
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', updateDropdownPosition);
      });
    } else {
      unlockBodyScroll();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateDropdownPosition);
    }
  });
  
  const handleScroll = () => {
    if (isOpen.value) {
      isOpen.value = false;
    }
  };
  
  onMounted(() => {
    const handleClickOutside = (event) => {
      if (containerRef.value && !containerRef.value.contains(event.target)) {
        isOpen.value = false;
      }
    };
    document.addEventListener('click', handleClickOutside);
    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateDropdownPosition);
      if (isOpen.value) {
        unlockBodyScroll();
      }
    });
  });
  
  const handleClear = (event) => {
    if (props.disabled) return;
    event.stopPropagation();
    emit('update:modelValue', '');
  };
  
  defineExpose({ focus: () => input.value?.focus() });
  </script>
  
  <template>
    <div class="select" ref="containerRef">
      <div
        :id="id"
        :class="[
          ...selectClasses,
          {
            'select__select--open': isOpen,
            'select__select--has-value': modelValue,
            'select__select--disabled': disabled || loading,
            'select__select--loading': loading
          }
        ]"
        @click="toggleDropdown"
        :data-testid="dataTestId"
      >
        <div class="select__value">
          <div v-if="showProfilePicture && selectedOptionWithImage" class="select__value-with-avatar">
            <div :class="avatarClasses">
              <img
                v-if="selectedOptionWithImage.profilePicture"
                :src="selectedOptionWithImage.profilePicture"
                alt=""
                class="select__value-avatar-img"
              >
              <User v-else class="w-full h-full p-1 text-white" />
            </div>
            <span class="select__value-label truncate">{{ displayValue }}</span>
          </div>
          <span v-else :class="{ 'text-white/50': !modelValue }" class="truncate">
            {{ displayValue }}
          </span>
        </div>
        <div class="select__actions">
          <div v-if="loading" class="select__loading-spinner">
            <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <button
            v-if="clearable && modelValue && !disabled && !loading"
            type="button"
            class="select__clear-button"
            @click="handleClear"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              class="select__clear-icon"
            >
              <path
                d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"
              />
            </svg>
          </button>
          <div class="select__caret">
            <svg
              class="select__caret-icon"
              :class="{
                'select__caret-icon--open': isOpen
              }"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>

      <teleport to="body">
        <div
          v-show="isOpen && !disabled && !loading"
          ref="dropdownRef"
          :class="[
            ...dropdownClasses,
            isDropdownUp ? 'select__dropdown--up' : 'select__dropdown--down'
          ]"
          :style="dropdownStyles"
        >
          <input
            v-if="!hideSearch"
            ref="input"
            type="text"
            class="select__search"
            v-model="searchQuery"
            placeholder="Search..."
            @click.stop
          >
          <div :class="['select__options', { 'select__options--no-search': hideSearch }]">
            <template v-if="filteredOptions?.length">
              <button
                v-for="option in filteredOptions"
                :key="option?.value"
                type="button"
                :class="['select__option', `hover:${optionHoverBgClass}`]"
                @click="handleSelect(option)"
              >
                <div v-if="showProfilePicture" :class="optionAvatarClasses">
                  <img
                    v-if="option?.profilePicture"
                    :src="option?.profilePicture"
                    alt=""
                    class="select__option-avatar-img"
                  >
                  <User v-else class="w-full h-full p-1 text-white" />
                </div>
                <span class="select__option-label truncate" data-testid="select-option-label">
                  {{ option?.label }}
                </span>
              </button>
            </template>
            <div v-else class="select__no-options">
              No options available
            </div>
          </div>
        </div>
      </teleport>
    </div>
  </template>
  
  <style scoped lang="postcss">
  .select {
    @apply relative w-full ;
  }

  
  .select__select {
    @apply flex items-center justify-between w-full px-4 py-2.5 rounded-[14px] h-[30px] 
      transition-all duration-300 ease-in-out cursor-pointer
      focus:outline-none;
    background-color: #0F0F0F;
    border: 1px solid #232323 !important;
    &:hover:not(.select__select--disabled):not(.select__select--loading) {
      background: #232323;
      border-color: #393939 !important;
    }
  }
  
  .select__select--open {
    @apply border-echoes-primary;
  }
  
  .select__select--disabled {
    @apply opacity-50 cursor-not-allowed;
  }
  
  .select__value {
    @apply text-xs md:text-sm flex-1 pr-2 overflow-hidden;
  }
  
  .select__value-with-avatar {
    @apply flex items-center w-full overflow-hidden;
  }
  
  .select__value-avatar {
    @apply w-6 h-6 rounded-full overflow-hidden mr-2 flex-shrink-0;
  }
  
  .select__value-avatar-img {
    @apply w-full h-full object-cover;
  }
  
  .select__value-label {
    @apply flex-1 overflow-hidden;
  }
  
  .select__placeholder {
    @apply text-white/50 hover:text-white/80 text-xs md:text-sm flex items-center;
  }
  
  .select__caret {
    @apply flex items-center ml-2 flex-shrink-0;
  }
  
  .select__caret-icon {
    @apply w-5 h-5 text-white/80 transition-transform duration-200;
  }
  
  .select__caret-icon--open {
    @apply transform rotate-180;
  }
  
  .select__dropdown {
    @apply fixed z-[999999]
      border
      rounded-[13px] shadow-sm
      overflow-hidden;
  }
    
  .select__search {
    @apply w-full px-4 py-2 text-xs md:text-sm
      bg-transparent
      text-white 
      border-b border-echoes-border-gray
      placeholder-white/50 hover:placeholder-white/80;
  
    &:focus {
      @apply outline-none;
    }
  }
  
  .select__options {
    @apply max-h-[160px] overflow-y-auto;
  
    &.select__options--no-search {
      @apply border-t-0;
    }
  }
  
  .select__option {
    @apply w-full px-4 py-2.5 text-left text-xs md:text-sm h-11
      text-white 
      transition-colors duration-150 ease-in-out
      flex items-center overflow-hidden;
  }
  
  .select__option-avatar {
    @apply w-6 h-6 rounded-full overflow-hidden mr-2 flex-shrink-0;
  }
  
  .select__option-avatar-img {
    @apply w-full h-full object-cover;
  }
  
  .select__option-label {
    @apply flex-1 overflow-hidden;
  }
  
  .select__no-options {
    @apply px-4 py-2.5 text-xs md:text-sm text-white/80;
  }
  
  .select__actions {
    @apply flex items-center gap-1 flex-shrink-0;
  }
  
  .select__clear-button {
    @apply rounded-full hover:bg-echoes-background-gray/50
      transition-colors duration-150;
  }
  
  .select__clear-icon {
    @apply w-4 h-4 text-white/80;
  }
  
  .select__select--loading {
    @apply opacity-75 cursor-wait;
  }
  
  .select__loading-spinner {
    @apply flex items-center ml-2 flex-shrink-0;
  }
  
  .select__loading-spinner svg {
    @apply w-5 h-5 text-white/80;
  }
  </style>
  
  
  